(() => {
  const API_NAME = 'NordwegShoppableRailLoader';
  const CONFIG_SELECTOR = 'script[data-shoppable-media-rail-loader]';
  const RAIL_SELECTOR = 'shoppable-media-rail';
  const CONTROL_SELECTOR = [
    '[data-rail-previous]',
    '[data-rail-next]',
    '[data-lookbook-open]',
    '[data-rail-video-toggle]',
    '[data-lookbook-view-more]',
  ].join(', ');

  if (window[API_NAME]?.refresh) {
    window[API_NAME].refresh();
    return;
  }

  const observedSections = new WeakSet();
  const nearSections = new Set();
  const dataQuery = window.matchMedia?.('(prefers-reduced-data: reduce)');
  const connection = typeof navigator !== 'undefined' ? navigator.connection : null;
  let observer = null;
  let runtimeUrl = '';
  let runtimePromise = null;
  let fallbackProximityPending = false;

  function isReady() {
    return Boolean(customElements.get('shoppable-media-rail'));
  }

  function prefersReducedData() {
    return Boolean(dataQuery?.matches || connection?.saveData);
  }

  function configurations() {
    return [...document.querySelectorAll(CONFIG_SELECTOR)];
  }

  function currentConfig() {
    const configs = configurations();
    return configs[configs.length - 1] || null;
  }

  function load() {
    if (isReady()) return Promise.resolve(customElements.get('shoppable-media-rail'));
    if (runtimePromise) return runtimePromise;

    const config = currentConfig();
    runtimeUrl = config?.dataset.shoppableMediaRailScript || runtimeUrl;
    if (!runtimeUrl) return Promise.reject(new Error('Shoppable media rail runtime URL is unavailable.'));

    runtimePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = runtimeUrl;
      script.async = true;
      script.dataset.shoppableMediaRailRuntime = '';

      const settle = (event) => {
        script.removeEventListener('load', settle);
        script.removeEventListener('error', settle);
        if (event.type === 'error') {
          script.remove();
          reject(new Error(`Failed to load shoppable media rail runtime: ${runtimeUrl}`));
          return;
        }
        if (!isReady()) {
          reject(new Error('Shoppable media rail runtime loaded without registering its element.'));
          return;
        }
        resolve(customElements.get('shoppable-media-rail'));
      };

      script.addEventListener('load', settle);
      script.addEventListener('error', settle);
      (document.head || document.documentElement).appendChild(script);
    }).then((constructor) => {
      observer?.disconnect();
      nearSections.clear();
      fallbackProximityPending = false;
      return constructor;
    }).catch((error) => {
      runtimePromise = null;
      throw error;
    });

    return runtimePromise;
  }

  function loadFromProximity() {
    if (prefersReducedData()) return;
    load().catch(() => false);
  }

  function observeSection(section) {
    if (!section || observedSections.has(section) || isReady()) return;
    observedSections.add(section);
    observer.observe(section);
  }

  function refresh() {
    const configs = configurations();
    const latestConfig = configs[configs.length - 1];
    runtimeUrl = latestConfig?.dataset.shoppableMediaRailScript || runtimeUrl;
    if (isReady() || configs.length === 0) return;

    if (configs.some((config) => config.dataset.designMode === 'true')) {
      load().catch(() => false);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      fallbackProximityPending = true;
      loadFromProximity();
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) nearSections.add(entry.target);
          else nearSections.delete(entry.target);
        });
        if (nearSections.size > 0) loadFromProximity();
      }, { rootMargin: '1200px 0px', threshold: 0 });
    }

    configs.forEach((config) => observeSection(document.getElementById(config.dataset.railSectionId)));
  }

  function railFromTarget(target) {
    return target instanceof Element ? target.closest(RAIL_SELECTOR) : null;
  }

  function warmFromIntent(event) {
    if (!railFromTarget(event.target) || isReady()) return;
    load().catch(() => false);
  }

  function shouldReplayClick(event, control) {
    if (event.defaultPrevented || event.button !== 0) return false;
    if (!control.matches('a[href]')) return true;
    return !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  function handleControlClick(event) {
    const control = event.target instanceof Element ? event.target.closest(CONTROL_SELECTOR) : null;
    if (!control || !railFromTarget(control) || isReady() || !shouldReplayClick(event, control)) return;

    event.preventDefault();
    const fallbackUrl = control.matches('a[href]') ? control.href : '';
    load()
      .then(() => control.click())
      .catch((error) => {
        console.error(error);
        if (fallbackUrl) window.location.assign(fallbackUrl);
      });
  }

  function handleDataPreferenceChange() {
    if (prefersReducedData()) return;
    if (nearSections.size > 0 || fallbackProximityPending) loadFromProximity();
  }

  const api = { isReady, load, refresh };
  window[API_NAME] = api;

  document.addEventListener('pointerover', warmFromIntent, { capture: true, passive: true });
  document.addEventListener('pointerdown', warmFromIntent, { capture: true, passive: true });
  document.addEventListener('touchstart', warmFromIntent, { capture: true, passive: true });
  document.addEventListener('focusin', warmFromIntent, true);
  document.addEventListener('click', handleControlClick, true);
  document.addEventListener('shopify:section:load', refresh);
  dataQuery?.addEventListener('change', handleDataPreferenceChange);
  connection?.addEventListener?.('change', handleDataPreferenceChange);

  refresh();
})();
